import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Shield } from "lucide-react";
import { API_ENDPOINTS } from "../config/api";
import { featureFlags } from "../config/featureFlags";
import { useI18n } from "../i18n";
import { Button } from "../components/ui/Button";
import { Select } from "../components/ui/Select";
import { TextField } from "../components/ui/TextField";
import ControlEvidenceBoard, {
  type ControlEvidenceRow,
  type LinkedEvidenceItem,
} from "../components/certification/ControlEvidenceBoard";
import portalStyles from "../styles/pages/SupplierPortalView.module.css";

type PortalItem = {
  id: string;
  title?: string | null;
  itemType?: string | null;
  status?: string | null;
  controlCode?: string | null;
  requirementCode?: string | null;
};

type ControlRow = {
  controlKey: string;
  title?: string;
  organizationSelfAssessment?: string;
  organizationReason?: string | null;
  auditorReviewStatus?: string;
  auditorComment?: string | null;
  linkedEvidence?: LinkedEvidenceItem[];
  linkedEvidenceCount?: number;
  linkedEvidenceIds?: string[];
};

type FindingRow = {
  id: string;
  title: string;
  description?: string | null;
  type?: string;
  severity?: string;
  status?: string;
  controlKey?: string | null;
  remediationId?: string | null;
};

const TABS = ["overview", "scope", "controls", "evidence", "evidenceRequests", "findings", "decision", "timeline"] as const;
type TabKey = (typeof TABS)[number];

const EVIDENCE_REQUEST_TYPES = ["POLICY", "PROCEDURE", "CONFIGURATION", "REPORT", "SCAN_REPORT", "LOG", "EXPORT", "SCREENSHOT", "REGISTER", "PLATFORM_EVIDENCE", "OTHER"] as const;
const REVIEW_STATUSES = ["NOT_REVIEWED", "IN_REVIEW", "ACCEPTED", "CLARIFICATION_REQUIRED", "ADDITIONAL_EVIDENCE_REQUIRED", "MINOR_NON_CONFORMITY", "MAJOR_NON_CONFORMITY", "NOT_APPLICABLE_CONFIRMED"] as const;
const EMPTY_EVIDENCE_REQUEST = {
  title: "",
  description: "",
  nativeControlKey: "",
  questionId: "",
  questionTitle: "",
  evidenceType: "SCAN_REPORT",
  status: "open",
};

function readTokenFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return String(params.get("token") || "");
}

function requestMeta(request: any) {
  const meta = request?.metadata && typeof request.metadata === "object" ? request.metadata : {};
  const linked = request?.linkedEvidence && typeof request.linkedEvidence === "object" ? request.linkedEvidence : null;
  return {
    evidenceType: String(request?.evidenceType || meta.evidenceType || "OTHER").toUpperCase(),
    nativeControlKey: request?.nativeControlKey || request?.controlCode || meta.nativeControlKey || "",
    questionKey: request?.questionId || request?.questionKey || meta.questionKey || "",
    questionTitle: request?.questionTitle || meta.questionTitle || "",
    legacyUnlinked: Boolean(request?.legacyUnlinked) || !(request?.nativeControlKey || request?.controlCode || meta.nativeControlKey),
    organizationComment:
      request?.organizationComment
      || meta.organizationComment
      || meta.clarificationText
      || "",
    evidenceSource: request?.evidenceSource || meta.evidenceSource || "",
    uploadedFileName: request?.uploadedFileName || meta.uploadedFileName || linked?.fileName || "",
    submittedAt: request?.respondedAt || meta.submittedAt || "",
    disposition: meta.auditDisposition || "",
    linkedEvidence: linked,
  };
}

export default function AuditorPortal() {
  const { t, language, setLanguage, allowedLanguages } = useI18n();
  const token = useMemo(() => readTokenFromUrl(), []);
  const [tab, setTab] = useState<TabKey>("overview");
  const [session, setSession] = useState<any>(null);
  const [pkg, setPkg] = useState<any>(null);
  const [scope, setScope] = useState<any>(null);
  const [items, setItems] = useState<PortalItem[]>([]);
  const [controls, setControls] = useState<ControlRow[]>([]);
  const [evidence, setEvidence] = useState<any[]>([]);
  const [findings, setFindings] = useState<FindingRow[]>([]);
  const [evidenceRequests, setEvidenceRequests] = useState<any[]>([]);
  const [decisions, setDecisions] = useState<any[]>([]);
  const [timeline, setTimeline] = useState<any[]>([]);
  const [status, setStatus] = useState<Record<string, string>>({});
  const [comment, setComment] = useState<Record<string, string>>({});
  const [evidenceReviewStatus, setEvidenceReviewStatus] = useState<Record<string, string>>({});
  const [evidenceReviewComment, setEvidenceReviewComment] = useState<Record<string, string>>({});
  const [savingControl, setSavingControl] = useState<string | null>(null);
  const [savingEvidenceItem, setSavingEvidenceItem] = useState<string | null>(null);
  const [savingEvidenceRequest, setSavingEvidenceRequest] = useState<string | null>(null);
  const [savingFinding, setSavingFinding] = useState<string | null>(null);
  const [decisionSaving, setDecisionSaving] = useState(false);
  const [loadingState, setLoadingState] = useState(true);
  const [findingDraft, setFindingDraft] = useState({
    controlKey: "",
    title: "",
    description: "",
    type: "OBSERVATION",
    severity: "MEDIUM",
  });
  const [evidenceRequestDraft, setEvidenceRequestDraft] = useState({ ...EMPTY_EVIDENCE_REQUEST });
  const [controlQuestions, setControlQuestions] = useState<any[]>([]);
  const [downloadingEvidenceId, setDownloadingEvidenceId] = useState<string | null>(null);
  const [legacyLinkDraft, setLegacyLinkDraft] = useState<Record<string, string>>({});
  const [decisionDraft, setDecisionDraft] = useState({
    decision: "CERTIFIED",
    conditions: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [exportState, setExportState] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [exportMessage, setExportMessage] = useState<string | null>(null);
  const [portalState, setPortalState] = useState<
    "idle" | "feature_disabled" | "missing_token" | "invalid_token" | "access_denied" | "active_session"
  >("idle");

  const headers = useMemo(
    () => ({ "Content-Type": "application/json", "x-auditor-token": token }),
    [token],
  );

  const evidenceTypeLabel = (value?: string | null) => {
    const type = String(value || "OTHER").toUpperCase();
    return t(`certification.evidenceTypes.${type}`, t(`governance.evidenceArtifacts.category.${type}`, type.replaceAll("_", " ")));
  };

  useEffect(() => {
    const load = async () => {
      setLoadingState(true);
      if (!featureFlags.AUDITOR_PORTAL_V1) {
        setPortalState("feature_disabled");
        setLoadingState(false);
        return;
      }
      if (!token) {
        setError(t("certification.portal.errors.missingToken", "Missing auditor token"));
        setPortalState("missing_token");
        setLoadingState(false);
        return;
      }
      const [sessionRes, packageRes, itemsRes, scopeRes, controlsRes, evidenceRes, findingsRes, requestsRes, decisionsRes, timelineRes] = await Promise.all([
        fetch(API_ENDPOINTS.AUDITOR_PORTAL_SESSION, { headers }),
        fetch(API_ENDPOINTS.AUDITOR_PORTAL_PACKAGE, { headers }),
        fetch(API_ENDPOINTS.AUDITOR_PORTAL_PACKAGE_ITEMS, { headers }),
        fetch(API_ENDPOINTS.AUDITOR_PORTAL_SCOPE, { headers }),
        fetch(API_ENDPOINTS.AUDITOR_PORTAL_CONTROLS, { headers }),
        fetch(API_ENDPOINTS.AUDITOR_PORTAL_EVIDENCE, { headers }),
        fetch(API_ENDPOINTS.AUDITOR_PORTAL_FINDINGS, { headers }),
        fetch(API_ENDPOINTS.AUDITOR_PORTAL_EVIDENCE_REQUESTS, { headers }),
        fetch(API_ENDPOINTS.AUDITOR_PORTAL_DECISION, { headers }),
        fetch(API_ENDPOINTS.AUDITOR_PORTAL_TIMELINE, { headers }),
      ]);
      const sessionData = await sessionRes.json().catch(() => ({}));
      const packageData = await packageRes.json().catch(() => ({}));
      const itemsData = await itemsRes.json().catch(() => ({}));
      const scopeData = scopeRes.ok ? await scopeRes.json().catch(() => ({})) : {};
      const controlsData = controlsRes.ok ? await controlsRes.json().catch(() => ({})) : {};
      const evidenceData = evidenceRes.ok ? await evidenceRes.json().catch(() => ({})) : {};
      const findingsData = findingsRes.ok ? await findingsRes.json().catch(() => ({})) : {};
      const requestsData = requestsRes.ok ? await requestsRes.json().catch(() => ({})) : {};
      const decisionsData = decisionsRes.ok ? await decisionsRes.json().catch(() => ({})) : {};
      const timelineData = timelineRes.ok ? await timelineRes.json().catch(() => ({})) : {};
      if (!sessionRes.ok) {
        setError(String(sessionData?.message || t("certification.portal.errors.openSession", "Failed to open auditor session")));
        if (sessionRes.status === 401) setPortalState("invalid_token");
        else if (sessionRes.status === 403) setPortalState("access_denied");
        setLoadingState(false);
        return;
      }
      setPortalState("active_session");
      setSession(sessionData?.data || null);
      if (sessionData?.data?.locale && allowedLanguages.includes(sessionData.data.locale)) {
        setLanguage(sessionData.data.locale);
      }
      setPkg(packageData?.data || null);
      setItems(Array.isArray(itemsData?.data) ? itemsData.data : []);
      setScope(scopeData?.data || null);
      setControls(Array.isArray(controlsData?.data) ? controlsData.data : []);
      setEvidence(Array.isArray(evidenceData?.data) ? evidenceData.data : []);
      setFindings(Array.isArray(findingsData?.data) ? findingsData.data : []);
      setEvidenceRequests(Array.isArray(requestsData?.data) ? requestsData.data : []);
      setDecisions(Array.isArray(decisionsData?.data) ? decisionsData.data : []);
      setTimeline(Array.isArray(timelineData?.data) ? timelineData.data : []);
      setLoadingState(false);
    };
    void load();
  }, [token, headers, t, allowedLanguages, setLanguage]);

  const reviewedCount = controls.filter((c) => c.auditorReviewStatus && c.auditorReviewStatus !== "NOT_REVIEWED").length;
  const openRequests = evidenceRequests.filter((request) => String(request.status || "").toLowerCase() !== "closed").length;
  const reviewStatusLabel = (value?: string | null) =>
    t(`certification.status.review.${String(value || "NOT_REVIEWED").toUpperCase()}`, String(value || "NOT_REVIEWED").replaceAll("_", " "));
  const findingStatusLabel = (value?: string | null) =>
    t(`certification.status.finding.${String(value || "OPEN").toUpperCase()}`, String(value || "OPEN").replaceAll("_", " "));
  const roleLabel = (value?: string | null) =>
    t(`certification.roles.${String(value || "AUDITOR").toUpperCase()}`, String(value || "AUDITOR").replaceAll("_", " "));
  const workflowStatusLabel = (value?: string | null) =>
    t(`certification.status.lifecycle.${String(value || "PREPARATION").toUpperCase()}`, t(`certification.status.workflow.${String(value || "DRAFT").toUpperCase()}`, String(value || "DRAFT").replaceAll("_", " ")));
  const controlOptions = controls.map((ctrl) => ({
    value: ctrl.controlKey,
    label: `${ctrl.controlKey}${ctrl.title ? ` · ${ctrl.title}` : ""}`,
  }));
  const formatOrgAssessment = (value?: string | null) =>
    t(`certification.status.assessment.${String(value || "UNASSESSED").toUpperCase()}`, String(value || "Unassessed").replaceAll("_", " "));

  const controlEvidenceRows = useMemo<ControlEvidenceRow[]>(() => {
    const evidenceById = new Map<string, LinkedEvidenceItem>();
    for (const ev of evidence) {
      if (!ev?.id) continue;
      evidenceById.set(String(ev.id), {
        id: String(ev.id),
        title: ev.title || null,
        description: ev.description || null,
        hasAttachment: Boolean(ev.hasAttachment),
        fileName: ev.fileName || null,
        verificationStatus: ev.verificationStatus || null,
        evidenceType: ev.evidenceType || ev.artifactCategory || null,
        artifactCategory: ev.artifactCategory || null,
      });
    }
    for (const req of evidenceRequests) {
      const linked = req?.linkedEvidence;
      if (linked?.id) {
        evidenceById.set(String(linked.id), {
          id: String(linked.id),
          title: linked.title || req.uploadedFileName || req.title || null,
          fileName: linked.fileName || req.uploadedFileName || null,
          hasAttachment: Boolean(linked.hasAttachment ?? linked.fileName),
          verificationStatus: linked.verificationStatus || null,
          evidenceType: linked.evidenceType || null,
        });
      }
    }

    return controls.map((ctrl) => {
      const key = String(ctrl.controlKey);
      const merged = new Map<string, LinkedEvidenceItem>();
      for (const row of ctrl.linkedEvidence || []) {
        if (row?.id) merged.set(String(row.id), row);
      }
      for (const id of ctrl.linkedEvidenceIds || []) {
        const sid = String(id);
        if (!merged.has(sid) && evidenceById.has(sid)) merged.set(sid, evidenceById.get(sid)!);
      }
      for (const req of evidenceRequests) {
        const reqControl = String(req.nativeControlKey || req.controlCode || req?.metadata?.nativeControlKey || "");
        if (reqControl !== key) continue;
        const linked = req.linkedEvidence;
        if (linked?.id) merged.set(String(linked.id), evidenceById.get(String(linked.id)) || {
          id: String(linked.id),
          title: linked.title || req.title || null,
          fileName: linked.fileName || req.uploadedFileName || null,
          hasAttachment: Boolean(linked.hasAttachment ?? linked.fileName),
          verificationStatus: linked.verificationStatus || null,
        });
      }
      for (const ev of evidence) {
        if (String(ev.controlCode || "") === key && ev.id && !merged.has(String(ev.id))) {
          merged.set(String(ev.id), evidenceById.get(String(ev.id))!);
        }
      }
      const linkedEvidence = Array.from(merged.values());
      const openRequestCount = evidenceRequests.filter((req) => {
        const reqControl = String(req.nativeControlKey || req.controlCode || req?.metadata?.nativeControlKey || "");
        const status = String(req.status || "").toLowerCase();
        return reqControl === key && !["closed", "accepted", "rejected"].includes(status);
      }).length;
      const findingCount = findings.filter((f) => String(f.controlKey || "") === key).length;
      return {
        controlKey: key,
        title: ctrl.title || null,
        organizationSelfAssessment: ctrl.organizationSelfAssessment || null,
        organizationReason: ctrl.organizationReason || null,
        auditorReviewStatus: ctrl.auditorReviewStatus || null,
        auditorComment: ctrl.auditorComment || null,
        linkedEvidenceCount: linkedEvidence.length,
        linkedEvidence,
        openRequestCount,
        findingCount,
      };
    });
  }, [controls, evidence, evidenceRequests, findings]);

  const refreshFindings = async () => {
    const res = await fetch(API_ENDPOINTS.AUDITOR_PORTAL_FINDINGS, { headers });
    const json = await res.json().catch(() => ({}));
    setFindings(Array.isArray(json?.data) ? json.data : []);
  };
  const refreshEvidenceRequests = async () => {
    const res = await fetch(API_ENDPOINTS.AUDITOR_PORTAL_EVIDENCE_REQUESTS, { headers });
    const json = await res.json().catch(() => ({}));
    setEvidenceRequests(Array.isArray(json?.data) ? json.data : []);
  };
  const refreshDecisions = async () => {
    const res = await fetch(API_ENDPOINTS.AUDITOR_PORTAL_DECISION, { headers });
    const json = await res.json().catch(() => ({}));
    setDecisions(Array.isArray(json?.data) ? json.data : []);
  };
  const refreshTimeline = async () => {
    const res = await fetch(API_ENDPOINTS.AUDITOR_PORTAL_TIMELINE, { headers });
    const json = await res.json().catch(() => ({}));
    setTimeline(Array.isArray(json?.data) ? json.data : []);
  };
  const loadControlQuestions = async (controlKey: string) => {
    if (!controlKey) {
      setControlQuestions([]);
      return;
    }
    const res = await fetch(API_ENDPOINTS.AUDITOR_PORTAL_CONTROL_QUESTIONS(controlKey), { headers });
    const json = await res.json().catch(() => ({}));
    setControlQuestions(Array.isArray(json?.data?.questions) ? json.data.questions : []);
  };

  const downloadEvidenceAttachment = async (evidenceId: string, fileName?: string | null) => {
    setDownloadingEvidenceId(evidenceId);
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.AUDITOR_PORTAL_EVIDENCE_DOWNLOAD(evidenceId), { headers });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        setError(String(json?.message || t("certification.portal.errors.evidenceDownloadFailed", "Failed to download evidence file")));
        return;
      }
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = fileName || `evidence-${evidenceId}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    } catch {
      setError(t("certification.portal.errors.evidenceDownloadFailed", "Failed to download evidence file"));
    } finally {
      setDownloadingEvidenceId(null);
    }
  };

  const shell = (children: ReactNode) => (
    <div className={portalStyles.page}>
      <div className={portalStyles.shell}>
        <div className={portalStyles.brandBar}>
          <Shield className="h-8 w-8 shrink-0 opacity-95" aria-hidden />
          <h1 className={portalStyles.brandTitle}>DefendSphere</h1>
        </div>
        {children}
      </div>
    </div>
  );

  if (!featureFlags.AUDITOR_PORTAL_V1) {
    return shell(<p className={portalStyles.subtitle}>{t("certification.portal.disabled", "Auditor portal is disabled by feature flags.")}</p>);
  }

  return shell(
    <>
      <div className={portalStyles.hero}>
        <Shield className={portalStyles.heroIcon} aria-hidden />
        <h2 className={portalStyles.pageTitle}>{t("certification.portal.title", "Auditor Portal")}</h2>
      </div>
      <p className={portalStyles.subtitle}>
        {session?.auditorName || session?.auditorEmail || t("certification.portal.missingTokenHelp", "Open this page with a valid ?token= parameter.")}
        {session?.auditorRole ? ` · ${t("certification.portal.role", "Role")}: ${roleLabel(session.auditorRole)}` : ""}
      </p>
      {portalState === "invalid_token" ? <p className={portalStyles.subtitle}>{t("certification.portal.invalidToken", "Invalid or expired token.")}</p> : null}
      {portalState === "access_denied" ? <p className={portalStyles.subtitle}>{t("certification.portal.accessDenied", "Access denied for provided token scope.")}</p> : null}
      {error ? <p className={portalStyles.subtitle}>{error}</p> : null}
      {success ? <p className={portalStyles.subtitle}>{success}</p> : null}
      {loadingState && portalState === "active_session" ? <p className={portalStyles.subtitle}>{t("common.loading", "Loading...")}</p> : null}

      {session && portalState === "active_session" ? (
        <>
          {pkg ? (
            <p className={portalStyles.subtitle}>
              {pkg.name || pkg.id} · {pkg.standardCode} · {workflowStatusLabel(pkg.status)}
            </p>
          ) : null}
          <div style={{ marginBottom: 16, maxWidth: 220 }}>
            <Select
              label={t("certification.portal.language", "Language")}
              value={language}
              onChange={(value) => setLanguage(String(value || "en") as "en" | "ru" | "es")}
              options={allowedLanguages.map((lang) => ({ value: lang, label: lang.toUpperCase() }))}
            />
          </div>
          <nav className={portalStyles.tabs} aria-label={t("certification.portal.title", "Auditor Portal")}>
            {TABS.map((key) => (
              <button
                key={key}
                type="button"
                className={`${portalStyles.tab} ${tab === key ? portalStyles.tabActive : ""}`}
                onClick={() => {
                  setTab(key);
                  setError(null);
                  setSuccess(null);
                }}
              >
                {t(
                  `certification.portal.tabs.${key}`,
                  key === "controls"
                    ? "Controls & Evidence"
                    : key === "evidence"
                      ? "Evidence package"
                      : key,
                )}
              </button>
            ))}
          </nav>
        </>
      ) : null}

      {tab === "overview" && portalState === "active_session" ? (
        <div className={portalStyles.panel}>
          <div className={portalStyles.panelHead}>
            <h3 className={portalStyles.panelTitle}>{t("certification.portal.tabs.overview", "Overview")}</h3>
            <Button
              type="button"
              size="small"
              color="primary"
              disabled={exportState === "loading" || !session?.certificationPackageId}
              onClick={async () => {
                setExportMessage(null);
                if (!session?.certificationPackageId) {
                  setExportState("error");
                  setExportMessage(t("certification.portal.export.noPackage", "No certification package linked."));
                  return;
                }
                setExportState("loading");
                try {
                  const res = await fetch(API_ENDPOINTS.AUDITOR_PORTAL_PACKAGE_EXPORT_BUNDLE, { headers });
                  const json = await res.json().catch(() => ({}));
                  if (!res.ok) {
                    setExportState("error");
                    setExportMessage(String(json?.message || t("certification.portal.export.failed", "Export failed")));
                    return;
                  }
                  const blob = new Blob([JSON.stringify(json?.data ?? json, null, 2)], { type: "application/json" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `auditor-export-${String(session.certificationPackageId).slice(0, 8)}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                  setExportState("done");
                  setExportMessage(t("certification.portal.export.downloaded", "Bundle downloaded."));
                } catch {
                  setExportState("error");
                  setExportMessage(t("certification.portal.export.networkError", "Network error while exporting."));
                }
              }}
            >
              {exportState === "loading" ? t("certification.portal.export.loading", "Exporting…") : t("certification.portal.export.button", "Export bundle")}
            </Button>
          </div>
          <div className={portalStyles.panelBody}>
            <p>{t("certification.portal.metrics.controlsReviewed", "Controls reviewed")}: {reviewedCount} / {controls.length || "—"}</p>
            <p>{t("certification.portal.metrics.evidenceDisclosed", "Evidence disclosed")}: {evidence.length}</p>
            <p>{t("certification.portal.metrics.packageItems", "Package items")}: {items.length}</p>
            <p>{t("certification.portal.metrics.findingsOpen", "Open findings")}: {findings.filter((item) => String(item.status || "").toUpperCase() !== "CLOSED").length}</p>
            <p>{t("certification.portal.metrics.evidenceRequestsOpen", "Open evidence requests")}: {openRequests}</p>
            {scope?.activeSnapshot ? (
              <p>
                {t("certification.portal.scopeVersion", "Scope")} v{scope.scopeVersion}: {(scope.activeSnapshot.informationSystems || []).length} {t("certification.portal.informationSystems", "information system(s)")}
              </p>
            ) : (
              <p>{t("certification.portal.scopePending", "Scope not yet approved by organization.")}</p>
            )}
            {session?.packageVersion ? <p>{t("certification.portal.packageVersion", "Package version")}: {session.packageVersion}</p> : null}
            {session?.baselineVersion ? <p>{t("certification.portal.baselineVersion", "Baseline version")}: v{session.baselineVersion}</p> : null}
            {session?.evidenceManifestVersion ? <p>{t("certification.portal.manifestVersion", "Evidence manifest version")}: v{session.evidenceManifestVersion}</p> : null}
            {exportMessage ? <p>{exportMessage}</p> : null}
          </div>
        </div>
      ) : null}

      {tab === "scope" && portalState === "active_session" ? (
        <div className={portalStyles.panel}>
          <div className={portalStyles.panelHead}>
            <h3 className={portalStyles.panelTitle}>{t("certification.portal.tabs.scope", "Scope")} v{scope?.scopeVersion || 0}</h3>
          </div>
          <div className={portalStyles.panelBody}>
            <div className={portalStyles.stack}>
              {(scope?.activeSnapshot?.informationSystems || []).map((system: any) => (
                <div key={system.id}>
                  <strong>{system.name || system.code || system.id}</strong>
                  <p>{t("certification.portal.systemEnvironment", "Environment")}: {system.environment || "—"}</p>
                  <p>{t("certification.portal.systemCriticality", "Criticality")}: {system.criticality || "—"}</p>
                </div>
              ))}
              {!scope?.activeSnapshot?.informationSystems?.length ? <p>{t("certification.portal.scopePending", "Scope not yet approved by organization.")}</p> : null}
            </div>
          </div>
        </div>
      ) : null}

      {tab === "controls" && portalState === "active_session" ? (
        <ControlEvidenceBoard
          mode="auditor"
          controls={controlEvidenceRows}
          emptyLabel={t("certification.portal.noControls", "No native controls in scope (link assessment session and approve scope).")}
          formatOrgAssessment={formatOrgAssessment}
          formatReviewStatus={reviewStatusLabel}
          reviewStatusOptions={REVIEW_STATUSES.map((value) => ({ value, label: reviewStatusLabel(value) }))}
          reviewDraft={status}
          commentDraft={comment}
          onReviewStatusChange={(controlKey, value) => setStatus((prev) => ({ ...prev, [controlKey]: value }))}
          onCommentChange={(controlKey, value) => setComment((prev) => ({ ...prev, [controlKey]: value }))}
          savingControlKey={savingControl}
          downloadingEvidenceId={downloadingEvidenceId}
          onDownloadEvidence={(id, fileName) => void downloadEvidenceAttachment(id, fileName)}
          onSaveReview={async (controlKey) => {
            setError(null);
            setSuccess(null);
            setSavingControl(controlKey);
            try {
              const res = await fetch(API_ENDPOINTS.AUDITOR_PORTAL_CONTROL_REVIEW(controlKey), {
                method: "PATCH",
                headers,
                body: JSON.stringify({
                  auditorReviewStatus: status[controlKey] || controls.find((c) => c.controlKey === controlKey)?.auditorReviewStatus,
                  comment: comment[controlKey] || null,
                }),
              });
              const json = await res.json().catch(() => ({}));
              if (!res.ok) {
                setError(String(json?.message || t("certification.portal.errors.controlSaveFailed", "Failed to save control review")));
              } else {
                setSuccess(t("certification.portal.success.controlSaved", "Control review saved"));
                const controlsRes = await fetch(API_ENDPOINTS.AUDITOR_PORTAL_CONTROLS, { headers });
                const controlsData = controlsRes.ok ? await controlsRes.json().catch(() => ({})) : {};
                setControls(Array.isArray(controlsData?.data) ? controlsData.data : []);
              }
            } finally {
              setSavingControl(null);
            }
          }}
          onRequestEvidence={async (controlKey) => {
            const ctrl = controls.find((row) => row.controlKey === controlKey);
            setEvidenceRequestDraft({
              ...EMPTY_EVIDENCE_REQUEST,
              nativeControlKey: controlKey,
              questionTitle: ctrl?.title || "",
              title: `${controlKey}`,
            });
            await loadControlQuestions(controlKey);
            setTab("evidenceRequests");
          }}
          labels={{
            orgAssessment: t("certification.portal.organizationAssessment", "Organization"),
            auditorAssessment: t("certification.portal.auditorAssessment", "Auditor"),
            reason: t("certification.portal.organizationReason", "Reason / comment"),
            evidence: t("certification.portal.tabs.evidence", "Evidence"),
            noEvidence: t("certification.portal.noLinkedEvidence", "No linked evidence artifacts yet."),
            openRequests: t("certification.portal.metrics.evidenceRequestsOpen", "Open evidence requests"),
            findings: t("certification.portal.tabs.findings", "Findings"),
            filters: t("certification.portal.filters", "Filters"),
            filterAssessment: t("certification.portal.filterAssessment", "Assessment status"),
            filterEvidence: t("certification.portal.filterEvidence", "Evidence presence"),
            filterReview: t("certification.portal.filterReview", "Auditor review"),
            download: t("certification.portal.downloadEvidence", "Download attachment"),
            loading: t("common.loading", "Loading…"),
            save: t("common.save", "Save"),
            requestEvidence: t("certification.portal.evidenceRequests.requestFromControl", "Request Evidence"),
            comment: t("certification.portal.commentPlaceholder", "Comment"),
          }}
        />
      ) : null}

      {tab === "evidence" && portalState === "active_session" ? (
        <div className={portalStyles.stack}>
          <p className={portalStyles.subtitle}>
            {t(
              "certification.portal.evidencePackageHint",
              "Package-level disclosed evidence. Per-control review with comments and attachments is on Controls & Evidence.",
            )}
          </p>
          {evidence.map((ev) => (
            <div key={ev.id} className={portalStyles.panel}>
              <div className={portalStyles.panelBody}>
                <strong>{ev.title || ev.id}</strong>
                <p>{evidenceTypeLabel(ev.evidenceType || ev.artifactCategory)} · {ev.verificationStatus || "—"}</p>
                {ev.controlCode ? <p>{t("certification.portal.control", "Control")}: {ev.controlCode}</p> : null}
                {ev.description ? <p>{ev.description}</p> : null}
                {ev.hasAttachment ? (
                  <div style={{ marginTop: 8 }}>
                    <Button
                      type="button"
                      size="small"
                      color="secondary"
                      disabled={downloadingEvidenceId === ev.id}
                      onClick={() => void downloadEvidenceAttachment(ev.id, ev.fileName)}
                    >
                      {downloadingEvidenceId === ev.id
                        ? t("common.loading", "Loading…")
                        : `${t("certification.portal.downloadEvidence", "Download attachment")}${ev.fileName ? `: ${ev.fileName}` : ""}`}
                    </Button>
                  </div>
                ) : (
                  <p className={portalStyles.subtitle}>
                    {t("certification.portal.noAttachment", "No file attachment on this evidence record.")}
                  </p>
                )}
              </div>
            </div>
          ))}
          {!evidence.length ? <p className={portalStyles.subtitle}>{t("certification.portal.noEvidence", "No evidence disclosed for this audit package.")}</p> : null}
          {items.map((item) => (
            <div key={item.id} className={portalStyles.panel}>
              <div className={portalStyles.panelHead}>
                <h3 className={portalStyles.panelTitle}>{item.title || item.itemType || t("certification.portal.packageItem", "Package Item")}</h3>
              </div>
              <div className={portalStyles.panelBody}>
                <p>{t("certification.portal.control", "Control")}: {item.controlCode || "—"}</p>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                  <Select
                    label={t("certification.portal.auditorAssessment", "Auditor")}
                    value={evidenceReviewStatus[item.id] || "not_reviewed"}
                    onChange={(value) => setEvidenceReviewStatus((prev) => ({ ...prev, [item.id]: String(value || "not_reviewed") }))}
                    options={["not_reviewed", "accepted", "rejected", "clarification_required", "additional_evidence_required"].map((value) => ({ value, label: value }))}
                  />
                  <TextField
                    label={t("certification.portal.commentPlaceholder", "Comment")}
                    value={evidenceReviewComment[item.id] || ""}
                    onChange={(value) => setEvidenceReviewComment((prev) => ({ ...prev, [item.id]: value }))}
                    placeholder={t("certification.portal.commentPlaceholder", "Comment")}
                  />
                  <Button
                    type="button"
                    size="small"
                    color="primary"
                    onClick={async () => {
                      setSavingEvidenceItem(item.id);
                      setError(null);
                      setSuccess(null);
                      try {
                        const res = await fetch(API_ENDPOINTS.AUDITOR_PORTAL_REVIEW_ITEM_STATUS(item.id), {
                          method: "POST",
                          headers,
                          body: JSON.stringify({
                            status: evidenceReviewStatus[item.id] || "not_reviewed",
                            reviewerComment: evidenceReviewComment[item.id] || null,
                          }),
                        });
                        const json = await res.json().catch(() => ({}));
                        if (!res.ok) {
                          setError(String(json?.message || t("certification.portal.errors.evidenceReviewFailed", "Failed to save evidence review")));
                        } else {
                          setSuccess(t("certification.portal.success.evidenceReviewed", "Evidence review saved"));
                        }
                      } finally {
                        setSavingEvidenceItem(null);
                      }
                    }}
                  >
                    {savingEvidenceItem === item.id ? t("common.saving", "Saving...") : t("common.save", "Save")}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {tab === "evidenceRequests" && portalState === "active_session" ? (
        <div className={portalStyles.stack}>
          <div className={portalStyles.panel}>
            <div className={portalStyles.panelHead}>
              <h3 className={portalStyles.panelTitle}>{t("certification.portal.evidenceRequests.create", "Create request")}</h3>
            </div>
            <div className={portalStyles.panelBody}>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                <TextField
                  label={t("certification.portal.evidenceRequests.title", "Request title")}
                  value={evidenceRequestDraft.title}
                  onChange={(value) => setEvidenceRequestDraft((prev) => ({ ...prev, title: value }))}
                  placeholder={t("certification.portal.evidenceRequests.title", "Request title")}
                />
                <Select
                  label={t("certification.portal.evidenceRequests.control", "Control")}
                  value={evidenceRequestDraft.nativeControlKey || null}
                  onChange={(value) => {
                    const next = String(value || "");
                    const ctrl = controls.find((row) => row.controlKey === next);
                    setEvidenceRequestDraft((prev) => ({
                      ...prev,
                      nativeControlKey: next,
                      questionId: "",
                      questionTitle: ctrl?.title || "",
                    }));
                    void loadControlQuestions(next);
                  }}
                  placeholder={t("certification.portal.evidenceRequests.control", "Control")}
                  options={controlOptions}
                />
                <Select
                  label={t("certification.portal.evidenceRequests.questionOptional", "Question (optional)")}
                  value={evidenceRequestDraft.questionId || null}
                  onChange={(value) => {
                    const q = controlQuestions.find((row) => String(row.questionId || row.questionKey) === String(value));
                    setEvidenceRequestDraft((prev) => ({
                      ...prev,
                      questionId: String(value || ""),
                      questionTitle: q?.title || "",
                    }));
                  }}
                  placeholder={t("certification.portal.evidenceRequests.questionOptional", "Question (optional)")}
                  options={controlQuestions.map((q) => ({
                    value: String(q.questionId || q.questionKey),
                    label: q.title || q.questionKey || q.questionId,
                  }))}
                />
                <Select
                  label={t("certification.portal.evidenceRequests.evidenceType", "Evidence type")}
                  value={evidenceRequestDraft.evidenceType}
                  onChange={(value) => setEvidenceRequestDraft((prev) => ({ ...prev, evidenceType: String(value || "OTHER") }))}
                  options={EVIDENCE_REQUEST_TYPES.map((value) => ({ value, label: evidenceTypeLabel(value) }))}
                />
              </div>
              <div style={{ marginTop: 12 }}>
                <TextField
                  multiline
                  rows={3}
                  label={t("certification.portal.evidenceRequests.description", "Request details")}
                  value={evidenceRequestDraft.description}
                  onChange={(value) => setEvidenceRequestDraft((prev) => ({ ...prev, description: value }))}
                  placeholder={t("certification.portal.evidenceRequests.description", "Request details")}
                />
              </div>
              <div style={{ marginTop: 12 }}>
                <Button
                  type="button"
                  size="small"
                  color="primary"
                  disabled={!evidenceRequestDraft.description || !evidenceRequestDraft.nativeControlKey || !evidenceRequestDraft.evidenceType || savingEvidenceRequest === "create"}
                  onClick={async () => {
                    setSavingEvidenceRequest("create");
                    setError(null);
                    setSuccess(null);
                    try {
                      const res = await fetch(API_ENDPOINTS.AUDITOR_PORTAL_EVIDENCE_REQUESTS, {
                        method: "POST",
                        headers,
                        body: JSON.stringify(evidenceRequestDraft),
                      });
                      const json = await res.json().catch(() => ({}));
                      if (!res.ok) {
                        const details = [json?.message, json?.code, json?.stage ? `stage=${json.stage}` : null]
                          .filter(Boolean)
                          .join(" · ");
                        setError(details || t("certification.portal.errors.requestCreateFailed", "Failed to create evidence request"));
                        return;
                      }
                      setSuccess(t("certification.portal.success.requestCreated", "Evidence request created"));
                      setEvidenceRequestDraft(EMPTY_EVIDENCE_REQUEST);
                      await refreshEvidenceRequests();
                      await refreshTimeline();
                    } finally {
                      setSavingEvidenceRequest(null);
                    }
                  }}
                >
                  {savingEvidenceRequest === "create" ? t("common.saving", "Saving...") : t("certification.portal.evidenceRequests.create", "Create request")}
                </Button>
              </div>
              {!controls.length ? <p className={portalStyles.panelIntro}>{t("certification.portal.evidenceRequests.questionRequired", "Approve a baseline with native controls before requesting evidence against a question.")}</p> : null}
            </div>
          </div>
          {evidenceRequests.map((request) => {
            const meta = requestMeta(request);
            return (
              <div key={request.id} className={portalStyles.panel}>
                <div className={portalStyles.panelHead}>
                  <h3 className={portalStyles.panelTitle}>{request.title}</h3>
                </div>
                <div className={portalStyles.panelBody}>
                  <p>{t(`certification.status.request.${String(request.status || "open").toUpperCase()}`, String(request.status || "open").replaceAll("_", " "))} · {evidenceTypeLabel(meta.evidenceType)}</p>
                  <p>{t("certification.portal.control", "Control")}: {meta.nativeControlKey || t("certification.portal.legacyUnlinkedControl", "Not linked")}</p>
                  <p>{t("certification.portal.evidenceRequests.question", "Standard question")}: {meta.questionKey ? `${meta.questionKey}${meta.questionTitle ? ` · ${meta.questionTitle}` : ""}` : t("certification.portal.legacyUnlinkedQuestion", "Not linked")}</p>
                  {meta.legacyUnlinked ? <p>{t("certification.portal.legacyRequest", "Legacy request — control not linked")}</p> : null}
                  <p>{request.description || "—"}</p>
                  {meta.organizationComment ? (
                    <p>
                      {t("certification.portal.evidenceRequests.organizationComment", "Organization comment")}: {meta.organizationComment}
                    </p>
                  ) : null}
                  {request.linkedEvidenceId || meta.linkedEvidence ? (
                    <div style={{ marginTop: 8 }}>
                      <p>
                        {t("certification.portal.evidenceRequests.submittedEvidence", "Submitted evidence")}:{" "}
                        {meta.linkedEvidence?.title || meta.uploadedFileName || request.linkedEvidenceId}
                      </p>
                      {meta.linkedEvidence?.verificationStatus ? (
                        <p>
                          {t("certification.portal.verificationStatus", "Verification")}: {meta.linkedEvidence.verificationStatus}
                        </p>
                      ) : null}
                      {meta.linkedEvidence?.hasAttachment || meta.uploadedFileName ? (
                        <div style={{ marginTop: 8 }}>
                          <Button
                            type="button"
                            size="small"
                            color="secondary"
                            disabled={!request.linkedEvidenceId || downloadingEvidenceId === request.linkedEvidenceId}
                            onClick={() =>
                              void downloadEvidenceAttachment(
                                String(request.linkedEvidenceId),
                                meta.linkedEvidence?.fileName || meta.uploadedFileName || null,
                              )
                            }
                          >
                            {downloadingEvidenceId === request.linkedEvidenceId
                              ? t("common.loading", "Loading…")
                              : `${t("certification.portal.downloadEvidence", "Download attachment")}${
                                  meta.linkedEvidence?.fileName || meta.uploadedFileName
                                    ? `: ${meta.linkedEvidence?.fileName || meta.uploadedFileName}`
                                    : ""
                                }`}
                          </Button>
                        </div>
                      ) : (
                        <p className={portalStyles.subtitle}>
                          {t("certification.portal.noAttachment", "No file attachment on this evidence record.")}
                        </p>
                      )}
                    </div>
                  ) : String(request.status || "").toLowerCase() === "responded" ? (
                    <p className={portalStyles.subtitle}>
                      {t(
                        "certification.portal.evidenceRequests.responseWithoutFile",
                        "Organization responded, but no evidence file was linked.",
                      )}
                    </p>
                  ) : null}
                  {meta.evidenceSource ? <p>{t("certification.portal.evidenceRequests.evidenceSource", "Evidence source")}: {t(`certification.status.source.${String(meta.evidenceSource).toUpperCase()}`, meta.evidenceSource)}</p> : null}
                  {meta.submittedAt ? <p>{t("certification.portal.evidenceRequests.submittedAt", "Submission date")}: {String(meta.submittedAt).slice(0, 19)}</p> : null}
                  {meta.disposition ? <p>{t("certification.portal.evidenceRequests.disposition", "Audit disposition")}: {t(`certification.status.request.${String(meta.disposition).toUpperCase()}`, meta.disposition)}</p> : null}
                  {meta.legacyUnlinked && ["open", "responded"].includes(String(request.status || "").toLowerCase()) ? (
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginTop: 12 }}>
                      <Select
                        label={t("certification.portal.evidenceRequests.control", "Control")}
                        value={legacyLinkDraft[request.id] || null}
                        onChange={(value) => setLegacyLinkDraft((prev) => ({ ...prev, [request.id]: String(value || "") }))}
                        options={controlOptions}
                      />
                      <Button
                        type="button"
                        size="small"
                        color="secondary"
                        disabled={!legacyLinkDraft[request.id] || savingEvidenceRequest === request.id}
                        onClick={async () => {
                          setSavingEvidenceRequest(request.id);
                          try {
                            const res = await fetch(API_ENDPOINTS.AUDITOR_PORTAL_EVIDENCE_REQUEST_LINK(request.id), {
                              method: "PATCH",
                              headers,
                              body: JSON.stringify({ nativeControlKey: legacyLinkDraft[request.id] }),
                            });
                            const json = await res.json().catch(() => ({}));
                            if (!res.ok) {
                              setError(String(json?.message || t("certification.portal.errors.requestUpdateFailed", "Failed to update evidence request")));
                              return;
                            }
                            setSuccess(t("certification.portal.success.legacyLinked", "Legacy request linked"));
                            await refreshEvidenceRequests();
                          } finally {
                            setSavingEvidenceRequest(null);
                          }
                        }}
                      >
                        {t("certification.portal.evidenceRequests.linkLegacy", "Link control")}
                      </Button>
                    </div>
                  ) : null}
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginTop: 12 }}>
                    <TextField
                      multiline
                      rows={2}
                      label={t("certification.portal.commentPlaceholder", "Comment")}
                      value={evidenceReviewComment[request.id] || ""}
                      onChange={(value) => setEvidenceReviewComment((prev) => ({ ...prev, [request.id]: value }))}
                      placeholder={t("certification.portal.commentPlaceholder", "Comment")}
                    />
                    <Button
                      type="button"
                      size="small"
                      color="primary"
                      onClick={async () => {
                        setSavingEvidenceRequest(request.id);
                        setError(null);
                        setSuccess(null);
                        try {
                          const res = await fetch(API_ENDPOINTS.AUDITOR_PORTAL_EVIDENCE_REQUEST_DISPOSITION(request.id), {
                            method: "POST",
                            headers,
                            body: JSON.stringify({ disposition: "accepted", comment: evidenceReviewComment[request.id] || "" }),
                          });
                          const json = await res.json().catch(() => ({}));
                          if (!res.ok) {
                            setError(String(json?.message || t("certification.portal.errors.requestUpdateFailed", "Failed to update evidence request")));
                            return;
                          }
                          setSuccess(t("certification.portal.success.requestUpdated", "Evidence request updated"));
                          await refreshEvidenceRequests();
                          await refreshTimeline();
                        } finally {
                          setSavingEvidenceRequest(null);
                        }
                      }}
                    >
                      {t("certification.portal.evidenceRequests.accept", "Accept")}
                    </Button>
                    <Button
                      type="button"
                      size="small"
                      color="danger"
                      onClick={async () => {
                        setSavingEvidenceRequest(request.id);
                        try {
                          const res = await fetch(API_ENDPOINTS.AUDITOR_PORTAL_EVIDENCE_REQUEST_DISPOSITION(request.id), {
                            method: "POST",
                            headers,
                            body: JSON.stringify({ disposition: "rejected", comment: evidenceReviewComment[request.id] || "" }),
                          });
                          const json = await res.json().catch(() => ({}));
                          if (!res.ok) {
                            setError(String(json?.message || t("certification.portal.errors.requestUpdateFailed", "Failed to update evidence request")));
                            return;
                          }
                          setSuccess(t("certification.portal.success.requestUpdated", "Evidence request updated"));
                          await refreshEvidenceRequests();
                        } finally {
                          setSavingEvidenceRequest(null);
                        }
                      }}
                    >
                      {t("certification.portal.evidenceRequests.reject", "Reject")}
                    </Button>
                    <Button
                      type="button"
                      size="small"
                      color="secondary"
                      onClick={async () => {
                        setSavingEvidenceRequest(request.id);
                        try {
                          const res = await fetch(API_ENDPOINTS.AUDITOR_PORTAL_EVIDENCE_REQUEST_DISPOSITION(request.id), {
                            method: "POST",
                            headers,
                            body: JSON.stringify({ disposition: "additional_evidence_required", comment: evidenceReviewComment[request.id] || "" }),
                          });
                          const json = await res.json().catch(() => ({}));
                          if (!res.ok) {
                            setError(String(json?.message || t("certification.portal.errors.requestUpdateFailed", "Failed to update evidence request")));
                            return;
                          }
                          setSuccess(t("certification.portal.success.requestUpdated", "Evidence request updated"));
                          await refreshEvidenceRequests();
                        } finally {
                          setSavingEvidenceRequest(null);
                        }
                      }}
                    >
                      {t("certification.portal.evidenceRequests.requestMore", "Request more evidence")}
                    </Button>
                    <Button
                      type="button"
                      size="small"
                      color="outline"
                      onClick={async () => {
                        setSavingEvidenceRequest(request.id);
                        try {
                          const res = await fetch(API_ENDPOINTS.AUDITOR_PORTAL_EVIDENCE_REQUEST_DISPOSITION(request.id), {
                            method: "POST",
                            headers,
                            body: JSON.stringify({ disposition: "clarification_required", comment: evidenceReviewComment[request.id] || "" }),
                          });
                          const json = await res.json().catch(() => ({}));
                          if (!res.ok) {
                            setError(String(json?.message || t("certification.portal.errors.requestUpdateFailed", "Failed to update evidence request")));
                            return;
                          }
                          setSuccess(t("certification.portal.success.requestUpdated", "Evidence request updated"));
                          await refreshEvidenceRequests();
                        } finally {
                          setSavingEvidenceRequest(null);
                        }
                      }}
                    >
                      {t("certification.portal.evidenceRequests.requestClarification", "Request clarification")}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
          {!evidenceRequests.length ? <p className={portalStyles.subtitle}>{t("certification.portal.evidenceRequests.empty", "No evidence requests yet.")}</p> : null}
        </div>
      ) : null}

      {tab === "findings" && portalState === "active_session" ? (
        <div className={portalStyles.stack}>
          <div className={portalStyles.panel}>
            <div className={portalStyles.panelHead}>
              <h3 className={portalStyles.panelTitle}>{t("certification.portal.findings.create", "Create finding")}</h3>
            </div>
            <div className={portalStyles.panelBody}>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                <Select
                  label={t("certification.portal.findings.controlKey", "Control key")}
                  value={findingDraft.controlKey || null}
                  onChange={(value) => setFindingDraft((prev) => ({ ...prev, controlKey: String(value || "") }))}
                  placeholder={t("certification.portal.findings.controlKey", "Control key")}
                  options={controlOptions}
                />
                <TextField
                  label={t("certification.portal.findings.title", "Finding title")}
                  value={findingDraft.title}
                  onChange={(value) => setFindingDraft((prev) => ({ ...prev, title: value }))}
                  placeholder={t("certification.portal.findings.title", "Finding title")}
                />
                <Select
                  label={t("certification.portal.findings.type", "Finding type")}
                  value={findingDraft.type}
                  onChange={(value) => setFindingDraft((prev) => ({ ...prev, type: String(value || "OBSERVATION") }))}
                  options={["OBSERVATION", "OPPORTUNITY_FOR_IMPROVEMENT", "MINOR_NON_CONFORMITY", "MAJOR_NON_CONFORMITY"].map((value) => ({
                    value,
                    label: t(`certification.findingTypes.${value}`, value.replaceAll("_", " ")),
                  }))}
                />
                <Select
                  label={t("certification.portal.findings.severity", "Severity")}
                  value={findingDraft.severity}
                  onChange={(value) => setFindingDraft((prev) => ({ ...prev, severity: String(value || "MEDIUM") }))}
                  options={["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((value) => ({
                    value,
                    label: t(`certification.severity.${value}`, value),
                  }))}
                />
              </div>
              <div style={{ marginTop: 12 }}>
                <TextField
                  multiline
                  rows={3}
                  label={t("certification.portal.findings.description", "Description")}
                  value={findingDraft.description}
                  onChange={(value) => setFindingDraft((prev) => ({ ...prev, description: value }))}
                  placeholder={t("certification.portal.findings.description", "Description")}
                />
              </div>
              <div style={{ marginTop: 12 }}>
                <Button
                  type="button"
                  size="small"
                  color="primary"
                  onClick={async () => {
                    setError(null);
                    setSuccess(null);
                    const res = await fetch(API_ENDPOINTS.AUDITOR_PORTAL_FINDINGS, {
                      method: "POST",
                      headers,
                      body: JSON.stringify(findingDraft),
                    });
                    const json = await res.json().catch(() => ({}));
                    if (!res.ok) {
                      setError(String(json?.message || t("certification.portal.errors.findingCreateFailed", "Failed to create finding")));
                      return;
                    }
                    setFindingDraft({ controlKey: "", title: "", description: "", type: "OBSERVATION", severity: "MEDIUM" });
                    setSuccess(t("certification.portal.success.findingCreated", "Finding created"));
                    await refreshFindings();
                    await refreshTimeline();
                  }}
                >
                  {t("certification.portal.findings.create", "Create finding")}
                </Button>
              </div>
            </div>
          </div>
          {findings.map((finding) => (
            <div key={finding.id} className={portalStyles.panel}>
              <div className={portalStyles.panelHead}>
                <h3 className={portalStyles.panelTitle}>{finding.title} · {findingStatusLabel(finding.status)}</h3>
              </div>
              <div className={portalStyles.panelBody}>
                <p>{t(`certification.findingTypes.${String(finding.type || "OBSERVATION").toUpperCase()}`, finding.type || "OBSERVATION")}</p>
                {finding.controlKey ? <p>{t("certification.portal.control", "Control")}: {finding.controlKey}</p> : null}
                {finding.description ? <p>{finding.description}</p> : null}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Button
                    type="button"
                    size="small"
                    color="secondary"
                    disabled={savingFinding === finding.id}
                    onClick={async () => {
                      setSavingFinding(finding.id);
                      setError(null);
                      setSuccess(null);
                      try {
                        const res = await fetch(API_ENDPOINTS.AUDITOR_PORTAL_FINDING(finding.id), {
                          method: "PATCH",
                          headers,
                          body: JSON.stringify({ status: "CLOSED" }),
                        });
                        const json = await res.json().catch(() => ({}));
                        if (!res.ok) {
                          setError(String(json?.message || t("certification.portal.errors.findingUpdateFailed", "Failed to update finding")));
                          return;
                        }
                        setSuccess(t("certification.portal.success.findingUpdated", "Finding updated"));
                        await refreshFindings();
                        await refreshTimeline();
                      } finally {
                        setSavingFinding(null);
                      }
                    }}
                  >
                    {savingFinding === finding.id ? t("common.saving", "Saving...") : t("certification.portal.findings.close", "Close")}
                  </Button>
                  <Button
                    type="button"
                    size="small"
                    color="outline"
                    disabled={savingFinding === finding.id}
                    onClick={async () => {
                      setSavingFinding(finding.id);
                      setError(null);
                      setSuccess(null);
                      try {
                        const res = await fetch(API_ENDPOINTS.AUDITOR_PORTAL_FINDING(finding.id), {
                          method: "PATCH",
                          headers,
                          body: JSON.stringify({ status: "WITHDRAWN" }),
                        });
                        const json = await res.json().catch(() => ({}));
                        if (!res.ok) {
                          setError(String(json?.message || t("certification.portal.errors.findingUpdateFailed", "Failed to update finding")));
                          return;
                        }
                        setSuccess(t("certification.portal.success.findingUpdated", "Finding updated"));
                        await refreshFindings();
                        await refreshTimeline();
                      } finally {
                        setSavingFinding(null);
                      }
                    }}
                  >
                    {savingFinding === finding.id ? t("common.saving", "Saving...") : t("certification.portal.findings.withdraw", "Withdraw")}
                  </Button>
                </div>
              </div>
            </div>
          ))}
          {!findings.length ? <p className={portalStyles.subtitle}>{t("certification.portal.findings.empty", "No findings recorded yet.")}</p> : null}
        </div>
      ) : null}

      {tab === "decision" && portalState === "active_session" ? (
        <div className={portalStyles.stack}>
          <div className={portalStyles.panel}>
            <div className={portalStyles.panelHead}>
              <h3 className={portalStyles.panelTitle}>{t("certification.portal.decision.submit", "Submit decision")}</h3>
            </div>
            <div className={portalStyles.panelBody}>
              <Select
                label={t("certification.workflowDetail.decisionStatus", "Decision status")}
                value={decisionDraft.decision}
                onChange={(value) => setDecisionDraft((prev) => ({ ...prev, decision: String(value || "CERTIFIED") }))}
                options={["CERTIFIED", "CERTIFIED_WITH_OBSERVATIONS", "NOT_CERTIFIED"].map((value) => ({ value, label: value }))}
              />
              <div style={{ marginTop: 12 }}>
                <TextField
                  multiline
                  rows={3}
                  label={t("certification.portal.decision.conditions", "Decision conditions")}
                  value={decisionDraft.conditions}
                  onChange={(value) => setDecisionDraft((prev) => ({ ...prev, conditions: value }))}
                  placeholder={t("certification.portal.decision.conditions", "Decision conditions")}
                />
              </div>
              <div style={{ marginTop: 12 }}>
                <Button
                  type="button"
                  size="small"
                  color="primary"
                  disabled={decisionSaving}
                  onClick={async () => {
                    setDecisionSaving(true);
                    setError(null);
                    setSuccess(null);
                    try {
                      const res = await fetch(API_ENDPOINTS.AUDITOR_PORTAL_DECISION, {
                        method: "POST",
                        headers,
                        body: JSON.stringify(decisionDraft),
                      });
                      const json = await res.json().catch(() => ({}));
                      if (!res.ok) {
                        setError(String(json?.message || t("certification.portal.errors.decisionFailed", "Failed to submit decision")));
                        return;
                      }
                      setSuccess(t("certification.portal.success.decisionSaved", "Decision submitted"));
                      await refreshDecisions();
                      await refreshTimeline();
                    } finally {
                      setDecisionSaving(false);
                    }
                  }}
                >
                  {decisionSaving ? t("common.saving", "Saving...") : t("certification.portal.decision.submit", "Submit decision")}
                </Button>
              </div>
            </div>
          </div>
          {decisions.map((decision) => (
            <div key={decision.id} className={portalStyles.panel}>
              <div className={portalStyles.panelBody}>
                <strong>{decision.decision}</strong>
                <p>{decision.decisionDate}</p>
                <p>{decision.decidedByEmail || "—"}</p>
              </div>
            </div>
          ))}
          {!decisions.length ? <p className={portalStyles.subtitle}>{t("certification.portal.decision.empty", "No decision submitted yet.")}</p> : null}
        </div>
      ) : null}

      {tab === "timeline" && portalState === "active_session" ? (
        <div className={portalStyles.stack}>
          <Button type="button" size="small" color="secondary" onClick={() => void refreshTimeline()}>{t("common.retry", "Retry")}</Button>
          {timeline.map((event) => (
            <div key={event.id} className={portalStyles.panel}>
              <div className={portalStyles.panelBody}>
                <strong>{event.title}</strong>
                <p>{t(`certification.status.event.${String(event.eventType || "").toUpperCase()}`, String(event.eventType || "").replaceAll("_", " "))}</p>
                <p>{event.createdAt}</p>
              </div>
            </div>
          ))}
          {!timeline.length ? <p className={portalStyles.subtitle}>{t("certification.portal.timeline.empty", "No timeline events available.")}</p> : null}
        </div>
      ) : null}
    </>,
  );
}
